import React, { useState, useEffect } from 'react';
import { crawlingService } from '../../services/crawlingService';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';
import CrawlingStatusBadge from './CrawlingStatusBadge';

const BatchRecrawlModal = ({ 
  isOpen, 
  onClose, 
  places = [], 
  onBatchStart = () => {},
  onBatchComplete = () => {} 
}) => {
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [batchAnalysis, setBatchAnalysis] = useState(null);
  const [forceRecrawl, setForceRecrawl] = useState(false);

  useEffect(() => {
    if (isOpen && places.length > 0) {
      analyzePlaces();
    }
  }, [isOpen, places]);

  const analyzePlaces = async () => {
    setAnalysisLoading(true);
    
    const placeIds = places.map(place => place.id);
    const { data, error } = await crawlingService.getBatchCrawlStatus(placeIds);
    
    if (data) {
      setBatchAnalysis(data);
      // Auto-select places that should be recrawled
      const shouldRecrawlIds = data.recommendations.should_recrawl.map(p => p.place_id);
      const neverCrawledIds = data.recommendations.never_crawled.map(p => p.place_id);
      setSelectedPlaces([...shouldRecrawlIds, ...neverCrawledIds]);
    } else {
      toast.error('Failed to analyze places for crawling');
    }
    
    setAnalysisLoading(false);
  };

  const handleSelectPlace = (placeId) => {
    setSelectedPlaces(prev => {
      if (prev.includes(placeId)) {
        return prev.filter(id => id !== placeId);
      } else {
        return [...prev, placeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPlaces.length === places.length) {
      setSelectedPlaces([]);
    } else {
      setSelectedPlaces(places.map(place => place.id));
    }
  };

  const handleSelectRecommended = () => {
    if (!batchAnalysis) return;
    
    const recommendedIds = [
      ...batchAnalysis.recommendations.should_recrawl.map(p => p.place_id),
      ...batchAnalysis.recommendations.never_crawled.map(p => p.place_id)
    ];
    
    setSelectedPlaces(recommendedIds);
  };

  const startBatchRecrawl = async () => {
    if (selectedPlaces.length === 0) {
      toast.error('Please select places to crawl');
      return;
    }

    setLoading(true);
    
    const { data, error } = await crawlingService.startBatchRecrawl({
      place_ids: selectedPlaces,
      force_recrawl: forceRecrawl
    });
    
    if (data) {
      toast.success(`Batch re-crawl started for ${data.summary.queued} places`);
      onBatchStart(data);
      onClose();
    } else {
      toast.error('Failed to start batch re-crawl');
    }
    
    setLoading(false);
  };

  const getPlaceRecommendation = (placeId) => {
    if (!batchAnalysis) return null;
    
    const { recommendations } = batchAnalysis;
    
    if (recommendations.never_crawled.find(p => p.place_id === placeId)) {
      return { type: 'never_crawled', text: 'Never crawled', color: 'bg-blue-100 text-blue-800' };
    }
    
    const shouldRecrawl = recommendations.should_recrawl.find(p => p.place_id === placeId);
    if (shouldRecrawl) {
      return { 
        type: 'should_recrawl', 
        text: `Recommended (${shouldRecrawl.recrawl_reason})`, 
        color: 'bg-orange-100 text-orange-800' 
      };
    }
    
    if (recommendations.active_crawls.find(p => p.place_id === placeId)) {
      return { type: 'active', text: 'Currently crawling', color: 'bg-green-100 text-green-800' };
    }
    
    if (recommendations.recent_crawls.find(p => p.place_id === placeId)) {
      return { type: 'recent', text: 'Recently crawled', color: 'bg-gray-100 text-gray-800' };
    }
    
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Batch Re-crawl Places</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {analysisLoading ? (
            <Loading />
          ) : batchAnalysis ? (
            <>
              {/* Summary */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Batch Analysis Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">
                      {batchAnalysis.summary.never_crawled}
                    </div>
                    <div className="text-gray-500">Never Crawled</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-600">
                      {batchAnalysis.summary.should_recrawl}
                    </div>
                    <div className="text-gray-500">Should Re-crawl</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">
                      {batchAnalysis.summary.active_crawls}
                    </div>
                    <div className="text-gray-500">Active Crawls</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-600">
                      {batchAnalysis.summary.recent_crawls}
                    </div>
                    <div className="text-gray-500">Recent Crawls</div>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={places.length > 0 && selectedPlaces.length === places.length}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-500">
                    {selectedPlaces.length > 0 ? `${selectedPlaces.length} selected` : 'Select all'}
                  </span>
                  
                  <button
                    onClick={handleSelectRecommended}
                    className="px-3 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded text-sm hover:bg-blue-100"
                  >
                    Select Recommended ({batchAnalysis.summary.never_crawled + batchAnalysis.summary.should_recrawl})
                  </button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={forceRecrawl}
                      onChange={(e) => setForceRecrawl(e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                    />
                    Force re-crawl all
                  </label>
                </div>
              </div>

              {/* Places List */}
              <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                {places.map((place) => {
                  const recommendation = getPlaceRecommendation(place.id);
                  
                  return (
                    <div key={place.id} className="p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={selectedPlaces.includes(place.id)}
                          onChange={() => handleSelectPlace(place.id)}
                          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        
                        <div className="ml-3 flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900 text-sm">
                                {place.display_name}
                              </h4>
                              
                              {place.formatted_address && (
                                <p className="text-xs text-gray-500 mt-1">
                                  📍 {place.formatted_address}
                                </p>
                              )}
                              
                              {recommendation && (
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full mt-2 ${recommendation.color}`}>
                                  {recommendation.text}
                                </span>
                              )}
                            </div>
                            
                            <div className="ml-4 text-right text-xs text-gray-500">
                              {place.website_uri && (
                                <div className="text-green-600 mb-1">🌐 Has Website</div>
                              )}
                              
                              {/* Show existing links count if available */}
                              {batchAnalysis.places.find(p => p.place_id === place.id)?.total_links > 0 && (
                                <div>
                                  {batchAnalysis.places.find(p => p.place_id === place.id).total_links} links found
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Failed to analyze places. Please try again.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {selectedPlaces.length} of {places.length} places selected
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
            >
              Cancel
            </button>
            
            <button
              onClick={startBatchRecrawl}
              disabled={loading || selectedPlaces.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              Start Batch Re-crawl {selectedPlaces.length > 0 && `(${selectedPlaces.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchRecrawlModal;